import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { assertNotNil } from '../../../utils/types';
import {
  IDashboardQuery,
  IDashboardQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { dashboardQuery } from '../../../utils/apollo/dashboards';
import useDashboardSelector from '../../../redux/selectorHooks/useDashboardSelector';
import useUpdateDashboardStore from '../utils/useUpdateDashboardStore';
import { useAppDispatch } from '../../../redux/hooks';
import DashboardInstance from '../DashboardInstance';
import useResetDashboardInstanceEffect from '../utils/useResetDashboardInstanceEffect';
import { populatePublishedFolder } from '../../../redux/slices/dashboards/dashboardInstance';
import { appRoutes } from '../../../path';
import DashboardInstanceLoader from '../components/DashboardInstanceLoader';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { dashboardViewed } from '../../../utils/analytics/events/dashboard/events';

const DashboardHistoryInstancePage = () => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const urlParams = useParams();
  const { dashboardBaseId, instanceId } = urlParams;
  assertNotNil(dashboardBaseId);
  assertNotNil(instanceId);

  useEffect(() => {
    track(
      pageViewed({
        category: 'dashboards',
        name: 'historical/filtered dashboard',
        id: `${dashboardBaseId}/${instanceId}`,
      }),
    );
  }, [dashboardBaseId, instanceId, track]);

  const publishedFolder = useDashboardSelector('publishedFolder');
  const isLoaded = useDashboardSelector('isLoaded');
  const updateDashboardStore = useUpdateDashboardStore();

  const handleLoadInstance = ({
    dashboard,
    folderOfDashboard,
  }: IDashboardQuery) => {
    if (!publishedFolder) {
      dispatch(populatePublishedFolder({ publishedFolder: folderOfDashboard }));
    }
    const lastVisitedDashboardPathname = appRoutes.dashboard.generateLink(
      dashboardBaseId,
      instanceId,
    );

    track(dashboardViewed(dashboard));

    updateDashboardStore({ dashboard, lastVisitedDashboardPathname });
  };

  // Fetch history instance of the dashboard
  const { loading: queryLoading } = useQuery<
    IDashboardQuery,
    IDashboardQueryVariables
  >(dashboardQuery, {
    variables: {
      id: instanceId,
      baseId: dashboardBaseId,
    },
    onCompleted: handleLoadInstance,
  });

  // Perform clean-up on unmount
  useResetDashboardInstanceEffect();

  // the check for a queryLoading is related to a lifecycle issue with
  // route navigation and dashboard redux store. The new dashboard instance
  // renders with previous data before useResetDashboardRouteEffect take effect
  // Similar hack exists in other places, tagged with: routing_issue_hack_2025-02-10
  if (!isLoaded || queryLoading) {
    return <DashboardInstanceLoader />;
  }

  return <DashboardInstance />;
};

export default DashboardHistoryInstancePage;
